import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { ShieldCheck, CaretRight, ArrowClockwise, WarningCircle, CheckCircle } from "@phosphor-icons/react";

/**
 * ISO 22000 Readiness Score widget.
 *
 * Renders a hero score + 5-pillar breakdown driven by GET /iso/readiness-score.
 * Each pillar links straight to the module that fixes the gap, and shop-floor
 * hints (rendered as inline chips) explain the score in plain English.
 */
const toneMap = {
  green: {
    ring: "text-emerald-500",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    bar: "bg-emerald-500",
    dot: "bg-emerald-500",
    heroBg: "bg-gradient-to-br from-emerald-500 to-emerald-600",
  },
  amber: {
    ring: "text-amber-500",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
    bar: "bg-amber-500",
    dot: "bg-amber-500",
    heroBg: "bg-gradient-to-br from-amber-500 to-amber-600",
  },
  rose: {
    ring: "text-rose-500",
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
    bar: "bg-rose-500",
    dot: "bg-rose-500",
    heroBg: "bg-gradient-to-br from-rose-500 to-rose-600",
  },
};

function ScoreRing({ score, tone, grade, size = 128 }) {
  const t = toneMap[tone] || toneMap.rose;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const dash = (pct / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="iso-score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#e2e8f0" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          fill="none"
          className={t.ring}
          stroke="currentColor"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`text-3xl font-bold ${t.text}`} data-testid="iso-score-value">
          {Math.round(score)}
        </div>
        <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${t.text}`}>
          Grade {grade}
        </div>
      </div>
    </div>
  );
}

function PillarBar({ pillar }) {
  const t = toneMap[pillar.tone] || toneMap.rose;
  return (
    <Link
      to={pillar.link || "#"}
      className={`block border ${t.border} bg-white rounded-lg p-4 hover:shadow-sm transition group`}
      data-testid={`iso-pillar-${pillar.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${t.dot}`} />
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-700">
            {pillar.label}
          </div>
        </div>
        <div className={`text-xs font-bold ${t.text}`}>{Math.round(pillar.score)}</div>
      </div>
      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${t.bar} transition-all`}
          style={{ width: `${Math.max(2, Math.min(100, pillar.score))}%` }}
        />
      </div>
      <div className="mt-2 text-xs text-slate-500">{pillar.detail}</div>
      {pillar.hints && pillar.hints.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {pillar.hints.slice(0, 2).map((h) => (
            <div
              key={h}
              className={`inline-flex items-start gap-1.5 text-[11px] ${t.text} ${t.bg} rounded px-2 py-1 w-full`}
            >
              <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
              <span className="leading-snug">{h}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        Weight {pillar.weight}% <CaretRight size={10} weight="bold" />
      </div>
    </Link>
  );
}

export default function IsoReadinessWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/iso/readiness-score");
      setData(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load readiness score");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="iso-readiness-loading">
        <div className="text-sm text-slate-500">Calculating ISO 22000 readiness…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-white border border-rose-200 rounded-lg p-6" data-testid="iso-readiness-error">
        <div className="text-sm text-rose-600">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const t = toneMap[data.overall.tone] || toneMap.rose;
  const generatedAt = new Date(data.generated_at).toLocaleString();

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid="iso-readiness-widget"
    >
      <div className={`px-5 py-4 ${t.heroBg} text-white flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center backdrop-blur-sm">
            <ShieldCheck size={20} weight="duotone" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-90">
              ISO 22000 Compliance
            </div>
            <div className="font-heading text-base font-semibold">Audit Readiness Score</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">
              Window
            </div>
            <div className="text-sm font-semibold">Last {data.window_days} days</div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white/15 hover:bg-white/25 text-xs font-medium transition disabled:opacity-50"
            data-testid="iso-readiness-refresh"
          >
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 p-5">
        <div className="flex flex-col items-center justify-center">
          <ScoreRing
            score={data.overall.score}
            tone={data.overall.tone}
            grade={data.overall.grade}
          />
          <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            {data.overall.tone === "green" ? (
              <CheckCircle size={14} weight="fill" className="text-emerald-500" />
            ) : (
              <WarningCircle size={14} weight="fill" className={t.text} />
            )}
            <span>
              {data.overall.tone === "green"
                ? "You're audit-ready"
                : data.overall.tone === "amber"
                ? "A few gaps to close"
                : "Immediate action needed"}
            </span>
          </div>
          <Link
            to="/inventory/iso"
            className="mt-2 text-xs font-medium text-emerald-700 hover:text-emerald-900"
            data-testid="iso-readiness-open"
          >
            Open ISO console →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.pillars.map((p) => (
            <PillarBar key={p.key} pillar={p} />
          ))}
        </div>
      </div>

      <div className="px-5 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500 flex items-center justify-between">
        <span>
          Score = weighted average across 5 pillars · rolling {data.window_days}-day window
        </span>
        <span data-testid="iso-readiness-generated-at">Refreshed {generatedAt}</span>
      </div>
    </div>
  );
}
